import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import './OfferKOCritDetails.scss';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import { MdOpenInFull } from 'react-icons/md';
import Modal from '@srm/ui/modal';
import { Controller, useForm } from 'react-hook-form';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import SelectControl from '@srm/ui/controls/SelectControl';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferKOCrit, ITableKOItem } from '@models/winnerSelection/ITableKO';
import { IOption } from '@srm/ui/types/IOption';
import { IOfferKOCritDetailsFormData } from './IOfferKOCritDetailsFormData';

export interface IOfferKOCritDetailsProps {
    tableKey: string;
    isEdit: boolean;
    demandId: string;
    offerId: string;
    disabledHint?: string;
    editButtonName?: string | null;
    value: IOfferKOCrit;
    rowData: ITableKOItem;
    onSubmit?: (value: IOfferKOCritDetailsFormData) => void;
}

/** Императивный хэндл: открыть модалку по клику на всю KO-ячейку. */
export interface IOfferKOCritDetailsHandle {
    open: () => void;
}

const OfferKOCritDetails = forwardRef<IOfferKOCritDetailsHandle, IOfferKOCritDetailsProps>(
    ({ tableKey, isEdit, disabledHint, editButtonName, demandId, offerId, value, rowData, onSubmit }, ref) => {
        const [showModal, setShowModal] = useState<boolean>(false);
        const [error, setError] = useState<string>();

        useImperativeHandle(ref, () => ({ open: () => setShowModal(true) }), []);
        const methods = useForm<IOfferKOCritDetailsFormData>({
            mode: 'onBlur',
        });
        const hasUnsavedInput = useUnsavedFormInput(methods);

        const { isSubmitting, isDirty } = methods.formState;

        useEffect(() => {
            const defaultValue: IOfferKOCritDetailsFormData = {
                critId: rowData.critId,
                result: value.result,
            };
            methods.reset(defaultValue);
        }, [value, showModal]);

        const onClick = () => {
            setShowModal(!showModal);
        };

        const onFormSubmit = async (form: IOfferKOCritDetailsFormData) => {
            setError(undefined);
            return WinnerSelectionService.saveTableKOOfferCrit(offerId, demandId, tableKey, form)
                .then(() => {
                    sendNotification({
                        message: 'Данные успешно сохранены',
                        variant: 'green',
                    });
                    setShowModal(!showModal);
                    onSubmit && onSubmit(form);
                })
                .catch((reason) => {
                    setError(reason);
                    console.log(reason);
                });
        };

        const results = useMemo(() => {
            return [
                { label: 'Допущен', value: 1 },
                { label: 'Не допущен', value: 0 },
            ] as IOption[];
        }, []);

        const errorLabel = error && (
            <div className="offer-ko-crit-details__error">
                <Hint role="alert" title={error} tone="danger" />
            </div>
        );

        const modalFooter = (
            <div className={'modal-dlg-buttons'}>
                <Button
                    type="submit"
                    form="offer-ko-crit-details-form"
                    loading={isSubmitting}
                    className={clsx('offer-ko-crit-details__button', 'offer-ko-crit-details__save-button')}
                    disabled={isSubmitting || !isDirty}
                >
                    Сохранить
                </Button>
            </div>
        );

        return (
            <>
                {/* pill — визуальный аффорданс + клик мышью; клик ловит и сама ячейка
                (.ws-ko-cell, role=button), и кнопка ниже. Клавиатурный контрол —
                ячейка (pill вне tab-order). */}
                <div className={clsx('offer-ko-crit-details')}>
                    <Button
                        type="button"
                        aria-label="Показать детали"
                        variant={'secondary'}
                        tabIndex={-1}
                        startAdornment={<MdOpenInFull size="12px" aria-hidden />}
                        onClick={onClick}
                    >
                        {editButtonName ?? 'Показать детали'}
                    </Button>
                </div>
                {showModal ? (
                    <Modal
                        size="xl"
                        onClose={() => setShowModal(false)}
                        hasUnsavedInput={() => isEdit && hasUnsavedInput()}
                        header={rowData.critName}
                        footer={isEdit ? modalFooter : null}
                    >
                        {errorLabel}
                        {!isEdit && disabledHint && (
                            <div className={'offer-ko-crit-details__hint'}>
                                <Hint title={disabledHint} tone="warning" maxWidth="100%" />
                            </div>
                        )}
                        <FormProviderControl
                            formMethods={methods}
                            id="offer-ko-crit-details-form"
                            className="offer-ko-crit-details__form"
                            onSubmit={onFormSubmit}
                        >
                            <HookFormProvider controller={Controller}>
                                <div className="offer-ko-crit-details__content">
                                    <div className="offer-ko-crit-details__row">
                                        <SelectControl
                                            label="Результат допуска"
                                            name="result"
                                            readOnly={true}
                                            disabled={!isEdit}
                                            highlightMenuItems={false}
                                            options={results}
                                            control={methods.control}
                                            required={true}
                                            rules={{ required: REQUIRED_MESSAGES.select }}
                                            setValue={(options) => {
                                                return options[0].value;
                                            }}
                                            getValue={(options: IOption[], value: any) => {
                                                return options.filter((x) => x.value === value);
                                            }}
                                        />
                                    </div>
                                </div>
                            </HookFormProvider>
                        </FormProviderControl>
                    </Modal>
                ) : null}
            </>
        );
    },
);

OfferKOCritDetails.displayName = 'OfferKOCritDetails';

export default OfferKOCritDetails;
